import { supabase } from '@/integrations/supabase/client';
import { gaPurchase } from '@/utils/googleAnalytics';
import { trackCheckoutCompleted } from '@/utils/mixpanel';

export interface PaidCheckoutReceipt {
  ga4_delivery?: 'server';
  status: 'paid'; transaction_id: string; value: number; currency: 'USD';
  tier: string; billing_period: string; surface?: 'desktop' | 'web'; marketing_consent: 'granted' | 'denied';
}
export function verifiedCheckoutReceipt(data: unknown, sessionId: string): PaidCheckoutReceipt | null {
  const receipt = data as Partial<PaidCheckoutReceipt> | null;
  if (!receipt || receipt.status !== 'paid' || receipt.transaction_id !== sessionId ||
      !/^cs_[A-Za-z0-9_]{5,200}$/.test(sessionId) || receipt.currency !== 'USD' ||
      typeof receipt.value !== 'number' || !Number.isFinite(receipt.value) || receipt.value < 0 ||
      (receipt.surface !== undefined && !['desktop', 'web'].includes(receipt.surface)) ||
      typeof receipt.tier !== 'string' || typeof receipt.billing_period !== 'string' ||
      !['granted', 'denied'].includes(receipt.marketing_consent || '')) return null;
  return receipt as PaidCheckoutReceipt;
}
export function createCheckoutReceiptReporter(deps: {
  lookup: (id: string) => Promise<unknown>;
  report: (receipt: PaidCheckoutReceipt, surface: string, userId: string) => void;
  wait: () => Promise<void>;
}) {
  const active = new Map<string, Promise<void>>();
  return (sessionId: string | null, surface: string, userId: string): Promise<void> => {
    if (!sessionId || !/^cs_[A-Za-z0-9_]{5,200}$/.test(sessionId)) return Promise.resolve();
    const key = `${userId}:${sessionId}`;
    const pending = active.get(key);
    if (pending) return pending;
    const work = (async () => {
      for (let attempt = 0; attempt < 4; attempt++) {
        try {
          const receipt = verifiedCheckoutReceipt(await deps.lookup(sessionId), sessionId);
          if (receipt) { deps.report(receipt, surface, userId); return; }
        } catch { /* Reporting failure never controls subscription access. */ }
        if (attempt < 3) await deps.wait();
      }
    })().finally(() => { active.delete(key); });
    active.set(key, work);
    return work;
  };
}
/** Runs independently of page access/redirect polling; all requests are bounded. */
export const reportCheckoutReceipt = createCheckoutReceiptReporter({
  lookup: async sessionId => {
    const { data, error } = await supabase.functions.invoke('checkout-conversion', {
      body: { session_id: sessionId }, signal: AbortSignal.timeout(10_000)
    });
    if (typeof window !== 'undefined' && new URLSearchParams(window.location?.search || '').get('tracking_debug') === '1') {
      console.info('[purchase-tracking]', error ? 'receipt_request_failed' : verifiedCheckoutReceipt(data, sessionId) ? 'receipt_verified' : 'receipt_not_verified');
    }
    if (error) return null;
    return data;
  },
  wait: () => new Promise(resolve => setTimeout(resolve, 2_000)),
  report: (receipt, surface, userId) => {
    if (!gaPurchase({ value: receipt.value, currency: receipt.currency, transactionId: receipt.transaction_id,
      plan: receipt.billing_period, tier: receipt.tier, surface: receipt.surface ?? 'web', marketingConsent: receipt.marketing_consent === 'granted', ga4Delivery: receipt.ga4_delivery })) return;
    // `surface` is the registered platform super property (web | desktop | server);
    // the return route that produced the receipt is the UI surface.
    trackCheckoutCompleted({ ui_surface: surface, checkout_surface: receipt.surface ?? 'web', userId, plan: receipt.billing_period, tier: receipt.tier,
      value: receipt.value, transaction_id: receipt.transaction_id, receipt_verified: true,
      marketing_consent: receipt.marketing_consent });
  }
});
